import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Layout, Menu, message } from "antd";
import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import SVG_ICON from "@/assets/icons/svg";
import { findMenuKeys } from "@/helper/menuHelper";
import { validateRoute } from "@/helper/routeHelper";
import { useMenuWithBadges } from "@/hooks/useMenuWithBadges";
import { envStorage } from "@/lib/storage/envIsolatedStorage";
import "./index.less";
import { useCommonStore, useSetDeliveryOrdersTotal } from "@/stores";

const { Sider } = Layout;

// Re-export types for backward compatibility
export type {
  SidebarChild,
  SidebarGroup,
  SidebarItem,
} from "@/routers/navigation-adapters";

import { useMount } from "ahooks";
import deliveryOrderService from "@/services/deliveryOrder";

// local storage key
const OPEN_KEYS_STORAGE_KEY = "sidebar_open_keys";

const AppSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { collapsed, setCollapsed } = useCommonStore();
  const [messageApi, contextHolder] = message.useMessage();
  // store the previous path for navigation change detection
  const prevPathRef = useRef(location.pathname);

  // Use menu items with badges
  const menuItemsWithBadges = useMenuWithBadges();

  const setDeliveryOrdersTotal = useSetDeliveryOrdersTotal();

  // initialize menu state from local storage, if not available, use empty array
  const [openKeys, setOpenKeys] = useState<string[]>(() => {
    try {
      const savedKeys = envStorage.getItem(OPEN_KEYS_STORAGE_KEY);
      return savedKeys && savedKeys !== "undefined" ? JSON.parse(savedKeys) : [];
    } catch {
      return [];
    }
  });

  // Keep a ref to current openKeys to avoid dependency issues
  const openKeysRef = useRef(openKeys);
  // Update ref when state changes
  useEffect(() => {
    openKeysRef.current = openKeys;
  }, [openKeys]);

  useMount(() => {
    deliveryOrderService.getDraftDeliveryOrdersCount().then((res) => {
      setDeliveryOrdersTotal(res.data);
    });
  });

  // handle path change and menu state updates
  const updateMenuOnPathChange = useCallback(() => {
    try {
      // get the menu key corresponding to the current path
      const keys = findMenuKeys(location.pathname);
      const currentPath = location.pathname;
      const prevPath = prevPathRef.current;
      const currentOpenKeys = openKeysRef.current;

      // if it's the first load, page refresh, or localStorage is cleared
      if (!currentOpenKeys || currentOpenKeys.length === 0) {
        // set the menu key corresponding to the current path
        setOpenKeys(keys.openKeys || []);
        // save to local storage
        try {
          envStorage.setItem(OPEN_KEYS_STORAGE_KEY, JSON.stringify(keys.openKeys || []));
        } catch (e) {
          console.error("Failed to save open keys to localStorage:", e);
        }
      }
      // if it's a navigation operation (path change)
      else if (prevPath !== currentPath) {
        // merge the opened menu and the menu key corresponding to the current path
        const newOpenKeys = [...currentOpenKeys];

        if (keys.openKeys && Array.isArray(keys.openKeys)) {
          keys.openKeys.forEach((key) => {
            if (!newOpenKeys.includes(key)) {
              newOpenKeys.push(key);
            }
          });
        }

        setOpenKeys(newOpenKeys);
        // save to local storage
        try {
          envStorage.setItem(OPEN_KEYS_STORAGE_KEY, JSON.stringify(newOpenKeys));
        } catch (e) {
          console.error("Failed to save open keys to localStorage:", e);
        }
      }

      // update the previous path
      prevPathRef.current = currentPath;
    } catch (error) {
      console.error("Error updating menu state:", error); // error message
      // reset to empty array to ensure UI doesn't crash
      setOpenKeys([]);
    }
  }, [location.pathname]);

  // initialize menu state and update when path changes
  useEffect(() => {
    updateMenuOnPathChange();
  }, [updateMenuOnPathChange]);

  // handle menu expansion state change
  const onOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
    // save to local storage
    try {
      envStorage.setItem(OPEN_KEYS_STORAGE_KEY, JSON.stringify(keys));
    } catch (e) {
      console.error("Failed to save open keys to localStorage:", e);
    }
  };

  // handle menu click
  const handleMenuClick = (key: string) => {
    const { isValid, message } = validateRoute(key);

    if (!isValid) {
      messageApi.warning(message);
      if (message === "Route not found.") {
        navigate("/404");
      }
      return;
    }

    // if the clicked item is a parent menu item, do not navigate
    if (key === "#") {
      return;
    }

    navigate(key);
  };

  // handle collapse state change
  const updateMenuOnCollapse = useCallback(() => {
    try {
      const currentOpenKeys = openKeysRef.current;
      if (!collapsed && (!currentOpenKeys || currentOpenKeys.length === 0)) {
        // if the sidebar is expanded and no menu is open, get the menu key corresponding to the current path
        const keys = findMenuKeys(location.pathname);
        const safeOpenKeys = keys.openKeys || [];
        setOpenKeys(safeOpenKeys);
        try {
          envStorage.setItem(OPEN_KEYS_STORAGE_KEY, JSON.stringify(safeOpenKeys));
        } catch (e) {
          console.error("Failed to save open keys to localStorage:", e);
        }
      }
    } catch (error) {
      console.error("Error handling collapse state change:", error);
    }
  }, [collapsed, location.pathname]);

  useEffect(() => {
    updateMenuOnCollapse();
  }, [updateMenuOnCollapse]);

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={setCollapsed}
      width={240}
      collapsedWidth={48}
      className="relative app-sidebar"
      style={{ background: "#172B4D" }}
      trigger={null} // Don't use default trigger
    >
      {contextHolder}
      <div
        className={clsx(
          "flex items-center mt-[24px] mb-[32px] transition-all duration-300",
          !collapsed ? "pl-[24px]" : "pl-0 justify-center",
        )}
      >
        <img
          src={SVG_ICON.EchoLogo}
          alt="Echo Logo"
          className={clsx(!collapsed ? "w-[88px] h-[32px]" : "w-[32px] h-[32px]")}
        />
      </div>

      <button
        type="button"
        className="toggle-button bg-white absolute w-[24px] h-[24px] z-1 top-[65px] cursor-pointer -right-3 flex items-center justify-center border-0"
        onClick={() => setCollapsed(!collapsed)}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {collapsed ? <RightOutlined /> : <LeftOutlined />}
      </button>
      {collapsed ? (
        <Menu
          mode="vertical"
          theme="dark"
          style={{ background: "#172B4D", border: 0 }}
          className="sidebar-menu-collapsed"
          items={menuItemsWithBadges}
          selectedKeys={findMenuKeys(location.pathname).selectedKeys}
          onClick={({ key, domEvent }) => {
            if (typeof key === "string") {
              domEvent.preventDefault();
              handleMenuClick(key);
            }
          }}
        />
      ) : (
        <Menu
          mode="inline"
          theme="dark"
          style={{ background: "#172B4D", border: 0 }}
          className="sidebar-menu-open"
          items={menuItemsWithBadges}
          selectedKeys={findMenuKeys(location.pathname).selectedKeys}
          openKeys={openKeys || []}
          onOpenChange={onOpenChange}
          onClick={({ key, domEvent }) => {
            if (typeof key === "string") {
              domEvent.preventDefault();
              handleMenuClick(key);
            }
          }}
        />
      )}
    </Sider>
  );
};
export default AppSidebar;
